import { useEffect, useId, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import type { RecurrenceScope } from '../../types/task';
import { Radio } from '../atoms/Radio/Radio';
import { Button } from '../atoms/Button/Button';
import { Text } from '../atoms/Typography/Text';
import { Dialog } from '../ui/Dialog/Dialog';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import './RecurrenceScope.css';

const SCOPES: readonly RecurrenceScope[] = ['occurrence', 'following', 'series'];

interface RecurrenceScopeFieldsetProps {
  value: RecurrenceScope;
  onChange: (scope: RecurrenceScope) => void;
}

/** "Nur diesen Termin / Diesen und alle folgenden / Gesamte Serie" — one radio group. */
export function RecurrenceScopeFieldset({ value, onChange }: RecurrenceScopeFieldsetProps) {
  const { t } = useTranslation();
  const name = useId();
  return (
    <fieldset className="recurrence-scope">
      <legend className="recurrence-scope__legend">{t('tasks.scope.legend')}</legend>
      {SCOPES.map((scope) => (
        <Radio
          key={scope}
          name={name}
          value={scope}
          label={t(`tasks.scope.${scope}`)}
          checked={value === scope}
          onChange={() => onChange(scope)}
          data-testid={`recurrence-scope-${scope}`}
        />
      ))}
    </fieldset>
  );
}

interface EditScopeDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onContinue: (scope: RecurrenceScope) => void;
}

/** Asked before editing a recurring task: which occurrences the edit applies to. */
export function EditScopeDialog({ isOpen, onCancel, onContinue }: EditScopeDialogProps) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<RecurrenceScope>('occurrence');
  useEffect(() => {
    if (isOpen) setScope('occurrence');
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      title={t('tasks.scope.editTitle')}
      size="sm"
      data-testid="edit-scope-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>{t('tasks.scope.cancel')}</Button>
          <Button variant="primary" onClick={() => onContinue(scope)}>{t('tasks.scope.continue')}</Button>
        </>
      }
    >
      <div className="recurrence-scope__body">
        <Text>{t('tasks.scope.editMessage')}</Text>
        <RecurrenceScopeFieldset value={scope} onChange={setScope} />
      </div>
    </Dialog>
  );
}

interface DeleteTaskDialogProps {
  isOpen: boolean;
  title: string;
  isRecurring: boolean;
  isDeleting?: boolean;
  onCancel: () => void;
  onConfirm: (scope: RecurrenceScope | undefined) => void;
}

/**
 * Delete confirmation. For a recurring task it also asks which occurrences to
 * delete (default: only this one).
 */
export function DeleteTaskDialog({ isOpen, title, isRecurring, isDeleting = false, onCancel, onConfirm }: DeleteTaskDialogProps) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<RecurrenceScope>('occurrence');
  useEffect(() => {
    if (isOpen) setScope('occurrence');
  }, [isOpen]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={isRecurring ? t('tasks.scope.deleteTitle') : t('tasks.deleteConfirm.title')}
      message={isRecurring ? t('tasks.scope.deleteMessage', { title }) : t('tasks.deleteConfirm.message', { title })}
      confirmLabel={isRecurring ? t('tasks.scope.confirmDelete') : t('tasks.deleteConfirm.confirm')}
      cancelLabel={t('tasks.deleteConfirm.cancel')}
      onCancel={onCancel}
      onConfirm={() => onConfirm(isRecurring ? scope : undefined)}
      isConfirming={isDeleting}
    >
      {isRecurring ? <RecurrenceScopeFieldset value={scope} onChange={setScope} /> : null}
    </ConfirmDialog>
  );
}
